import { useCallback, useEffect, useMemo, useState } from 'react';

import Typography from '@mui/material/Typography';

import { BENCHMARKS_SELECT } from './constants';
import { IBenchmarkProps } from './interface';

import { InputText } from '../../../input';
import {
  Popover,
  Stack,
  List,
  ListItemButton,
  IconButton,
  Button,
} from '@mui/material';
import { FiPlus } from '@react-icons/all-files/fi/FiPlus';
import { HiPencil } from '@react-icons/all-files/hi/HiPencil';
import { MdSearch } from '@react-icons/all-files/md/MdSearch';
import { HiOutlineChevronUp } from '@react-icons/all-files/hi/HiOutlineChevronUp';
import { HiOutlineChevronDown } from '@react-icons/all-files/hi/HiOutlineChevronDown';
import debounce from 'lodash.debounce';
export const withSingleBenchmark =
  (Component: any) =>
  ({
    userIsPrime = true,
    defaultSelectedBenchmarks,
    ...props
  }: IBenchmarkProps) => {
    if (!props.isSingle) return <Component {...props} />;

    const [selectedBenchmark, setSelectedBenchmark] = useState<{
      value: string;
      label: string;
    }>(
      defaultSelectedBenchmarks![0]
        ? {
            label: defaultSelectedBenchmarks![0],
            value: defaultSelectedBenchmarks![0],
          }
        : BENCHMARKS_SELECT[0]
    );

    useEffect(() => {
      props.onSelectedBenchmarksChange(selectedBenchmark.value as any);
    }, [selectedBenchmark]);

    const PRIME_BENCHMARKS = useMemo(() => {
      if (userIsPrime) return BENCHMARKS_SELECT;

      const availableBenchmarks = ['cdi', 'ibov'];

      return BENCHMARKS_SELECT.map((item) => ({
        ...item,
        exclusiveToPrime: !availableBenchmarks.includes(item.value),
      }));
    }, [BENCHMARKS_SELECT, userIsPrime]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [searchTerm, setSearchTerm] = useState('');

    const handleClick = (event: any) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const onSearch = (term: string) => {
      setSearchTerm(term);
    };

    const delayedSearch = useCallback(debounce(onSearch, 500), [onSearch]);

    const filteredBenchmarks = useMemo(() => {
      return PRIME_BENCHMARKS.filter((benchmark) =>
        benchmark.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [PRIME_BENCHMARKS, userIsPrime, searchTerm]);

    return (
      <>
        <Button
          data-testid="benchmark-button"
          variant="outlined"
          size="small"
          id="indicator"
          color="inherit"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          sx={{
            minWidth: 140,
            fontWeight: 500,
            height: 32,
            borderRadius: 2,
            border: '1px solid #0000003A',
          }}
          endIcon={open ? <HiOutlineChevronUp /> : <HiOutlineChevronDown />}
        >
          <Typography color="#A0A0A0" variant="body2">
            Indicador:&nbsp;
          </Typography>
          {selectedBenchmark.label}
        </Button>
        <Popover
          id="benchmark-menu"
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              minWidth: 255,
              height: 400,
            },
          }}
        >
          <Stack p={2} gap={1.5} height="100%">
            <Typography>Indicadores</Typography>
            {!!props.customBenchmarks?.length ? (
              <List>
                {props.customBenchmarks?.map((benchmark) => (
                  <ListItemButton
                    key={`benchmark-${benchmark.value}`}
                    selected={selectedBenchmark.value === benchmark.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBenchmark(benchmark);
                    }}
                    sx={{
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography color="primary">{benchmark.label}</Typography>
                    </Stack>
                    <IconButton
                      onClick={(e) => {
                        if (props.onUpdateCustomBenchmark) {
                          e.stopPropagation();
                          props.onUpdateCustomBenchmark();
                          handleClose();
                        }
                      }}
                    >
                      <HiPencil size={16} />
                    </IconButton>
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <>
                {!!props.customBenchmarksEnabled && (
                  <Button
                    sx={{
                      whiteSpace: 'nowrap',
                    }}
                    startIcon={<FiPlus size={16} />}
                    onClick={(e) => {
                      if (props.onUpdateCustomBenchmark) {
                        props.onUpdateCustomBenchmark();
                        handleClose();
                      }
                    }}
                  >
                    Criar benchmark da carteira
                  </Button>
                )}
              </>
            )}

            <InputText
              placeholder="Pesquise por indicador"
              onChange={(e) => {
                delayedSearch(e.target.value);
              }}
              InputProps={{
                startAdornment: <MdSearch size={24} />,
              }}
            />
            <Stack flexGrow={1} sx={{ overflowY: 'auto' }}>
              <List>
                {filteredBenchmarks.map((benchmark) => (
                  <ListItemButton
                    key={`benchmark-${benchmark.value}`}
                    sx={{
                      justifyContent: 'space-between',
                    }}
                    selected={selectedBenchmark.value === benchmark.value}
                    onClick={() => setSelectedBenchmark(benchmark)}
                  >
                    <Typography>{benchmark.label}</Typography>
                  </ListItemButton>
                ))}
              </List>
            </Stack>
          </Stack>
        </Popover>
      </>
    );
  };
